import React, { useEffect, useState } from "react";
import apiClient from "services/apiClient";
import { conditionalityForObservations } from "utils/conditionalityForObservations";
import mapToDropdownValues from "utils/mapDropdownValues";
import CustomCheckbox from "view/components/CheckBox";
import QuestionPrompt from "../QuestionPrompt";
import { isSameChoice } from "./choiceMatching";

interface CheckboxesProps {
  label: string;
  description?: string;
  error?: any;
  name: string;
  editMode?: boolean;
  value: any;
  data: any[];
  handleChange?: any;
  handleBlur?: any;
  touched?: any;
  required?: boolean;
  choicesByUrl: {
    url: string;
    valueName: string;
    titleName: string;
  } | null;

  visibleIf?: any;
  enableIf?: Boolean;
  setValueIf?: Boolean;
  setValueExpression?: Boolean;
  requiredIf?: Boolean;
  answers: any;
  columns?: number;
  panel?: any;
  element?: any;
}

export const Checkboxes: React.FC<CheckboxesProps> = ({
  label,
  description,
  error,
  name,
  editMode = true,
  value,
  data,
  handleChange,
  handleBlur,
  touched,
  required,
  choicesByUrl = null,
  visibleIf = true,
  enableIf = false,
  setValueIf,
  setValueExpression,
  requiredIf,
  answers,
  columns = 1,
  panel,
  element,
}) => {
  const isVisible = true;

  const isEnabled = false;

  if (!isVisible) {
    return;
  }

  const [option, setOption] = useState([]);

  const getChecboxOptions = async (choicesByUrl: any) => {
    try {
      const { data } = await apiClient.get(choicesByUrl.url);
      const dropdownOptions = mapToDropdownValues(
        data,
        choicesByUrl.valueName,
        choicesByUrl.titleName,
        choicesByUrl.valueName,
        choicesByUrl.titleName
      );
      setOption(dropdownOptions);
    } catch (error: any) {
      return [];
    }
  };
  useEffect(() => {
    if (choicesByUrl !== null && editMode) {
      getChecboxOptions(choicesByUrl);
    }
  }, [editMode]);

  const onChange = (selectedItem: any) => {
    // Ensure value is treated as an array, defaulting to an empty array if null or undefined
    const currentValues = Array.isArray(value) ? value : value == null ? [] : [value];

    let newValue = [...currentValues];

    const selectedIndex = currentValues.findIndex((item: any) =>
      isSameChoice(item, selectedItem, choicesByUrl)
    );

    if (selectedIndex === -1) {
      newValue.push(selectedItem);
    } else {
      newValue.splice(selectedIndex, 1);
    }

    handleChange(newValue);
  };

  return (
    <div
      className="flex flex-col flex-grow w-full rounded-lg dark:bg-bgtetriary bg-bgPrimaryLight p-4"
      id={`${element?.questionId}`}
    >
      <div className="relative flex flex-col py-2">
        <QuestionPrompt
          label={label}
          name={name}
          description={description}
          required={required}
          editMode={editMode}
        />
        {editMode ? (
          <>
            <div
              className={`grid w-full border-secondary focus:outline-none gap-1.5 px-3 py-2 rounded dark:bg-secondaryLight`}
              style={{
                gridTemplateColumns: `repeat(${columns === 0 ? 1 : columns}, minmax(0, 1fr))`,
              }}
            >
              {choicesByUrl !== null ? (
                <RenderCheckboxesFromApi
                  items={option}
                  value={value}
                  handleChange={onChange}
                  required={required}
                  choicesByUrl={choicesByUrl}
                  isEnabled={isEnabled}
                />
              ) : (
                <RenderSimpleCheckboxes
                  items={data}
                  value={value}
                  handleChange={onChange}
                  required={required}
                  isEnabled={isEnabled}
                />
              )}
            </div>
            <div className="flex justify-start items-center self-stretch flex-grow-0 flex-shrink-0 relative py-0.5">
              {touched && error && (
                <p className="flex-grow w-[1/2] text-xs text-left text-accent_1Dark">
                  {error}
                </p>
              )}
            </div>
          </>
        ) : (
          <>
            <div className="relative flex items-center self-stretch justify-start flex-grow-0 flex-shrink-0">
              {value && value.length > 0 ? (
                <p className="w-full max-w-full max-h-32 overflow-hidden text-overflow-ellipsis whitespace-pre-line text-[15px] text-left font-normal font-Overpass text-textAnswer dark:text-textMain ">
                  {value && Array.isArray(value)
                    ? choicesByUrl !== null
                      ? extractAndJoinByKey(value, choicesByUrl?.titleName)
                      : extractAndJoinByKey(value, "text")
                    : getItemLabel(value, choicesByUrl)}
                </p>
              ) : (
                <p className="text-textNoSelection font-Overpass font-normal text-[15px] italic">
                  (No Selection)
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
export default Checkboxes;

interface IChoiceByUrl {
  url: string;
  valueName: string;
  titleName: string;
}

interface ICheckboxItem {
  [key: string]: any;
}

interface RenderSimpleCheckboxesProps {
  items: ICheckboxItem[];
  value: any;
  handleChange: any;
  required: any;
  isEnabled: boolean;
}

const RenderSimpleCheckboxes: React.FC<RenderSimpleCheckboxesProps> = ({
  items,
  value,
  handleChange,
  required,
  isEnabled,
}) => {
  const isChecked = (item: any) => {
    return (Array.isArray(value) ? value : value == null ? [] : [value]).some(
      (selectedItem: any) => isSameChoice(selectedItem, item, null)
    );
  };

  return (
    <>
      {items &&
        items?.map((item: any, index) => {
          const itemLabel = getItemLabel(item, null);
          return (
            <CustomCheckbox
              className="disabled:cursor-not-allowed"
              disabled={isEnabled}
              key={index}
              onChange={() => {
                handleChange(item);
              }}
              checked={isChecked(item)}
              value={item}
              required={required}
              label={
                <span className="text-sm font-normal text-gray-800 font-overpass dark:text-caption">
                  {itemLabel}
                </span>
              }
              labelPlacement="end"
            />
          );
        })}
    </>
  );
};

interface RenderCheckboxesFromApiProps {
  items: ICheckboxItem[];
  value: any;
  handleChange: any;
  required: any;
  choicesByUrl: IChoiceByUrl;
  isEnabled: boolean;
}

const RenderCheckboxesFromApi: React.FC<RenderCheckboxesFromApiProps> = ({
  items,
  value,
  handleChange,
  required,
  choicesByUrl,
  isEnabled,
}) => {
  const isChecked = (item: any) => {
    return (Array.isArray(value) ? value : value == null ? [] : [value]).some(
      (selectedItem: any) => isSameChoice(selectedItem, item, choicesByUrl)
    );
  };

  return (
    <>
      {items.map((item: ICheckboxItem, index) => {
        const itemLabel = getItemLabel(item, choicesByUrl);
        return (
          <CustomCheckbox
            disabled={isEnabled}
            className="w-full disabled:cursor-not-allowed"
            key={index}
            onChange={(e: any) => {
              handleChange(item);
            }}
            checked={isChecked(item)}
            required={required}
            label={
              <span
                className={isEnabled ? "cursor-pointer" : "cursor-not-allowed"}
                style={{
                  fontSize: "15px",
                  fontWeight: "400",
                  color: "#2C3236",
                }}
              >
                {itemLabel}
              </span>
            }
            labelPlacement="end"
          />
        );
      })}
    </>
  );
};

function getItemLabel(item: any, choicesByUrl: any) {
  if (item && typeof item === "object") {
    return choicesByUrl !== null ? item[choicesByUrl.titleName] : item?.text;
  }
  return item;
}
function isItemChecked(item: any, value: any, choicesByUrl: any) {
  if (value === null) {
    return false;
  }

  if (typeof item === "object" && typeof value === "object") {
    return choicesByUrl !== null
      ? value[choicesByUrl.valueName] === item[choicesByUrl.titleName]
      : value?.value === item?.text;
  }

  return item === value;
}

function extractAndJoinByKey(dataArray: any[], keyToExtract: string) {
  // Map through the array, extract the key values if the item is an object, or use the item itself if it's a string
  let extractedValues = dataArray
    .map((item) => (typeof item === "object" ? item[keyToExtract] : item))
    .filter((value) => value !== undefined);

  // Join the extracted values into a single string
  let joinedString = extractedValues.join(", ");

  return joinedString;
}
